import React, { useState, useEffect, useCallback, useRef } from 'react';
import { satisfaction as api } from '../services/api';
import SatisfactionFormulaireEditor from './SatisfactionFormulaireEditor';
import SatisfactionReponseModal from './SatisfactionReponseModal';
import SatisfactionNouveauModal from './SatisfactionNouveauModal';
import SatisfactionTableauBord from './SatisfactionTableauBord';
import SatisfactionObservationModal from './SatisfactionObservationModal';
import SatisfactionCharte from './SatisfactionCharte';
import { EtatProjet } from './SatisfactionProjet';
import { NotePastille, etiquetteFormulaire, ISSUE_PROJET, CONTACT_PROJET, VehiculeEnvoi } from './SatisfactionCommun';
import './Satisfaction.css';

// Date courte : « 16/10 », l'année seulement si ce n'est pas l'année en
// cours. L'heure n'aide pas à traiter un avis : elle reste dans la fiche.
function fmtCourt(d) {
  if (!d) return '—';
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return '—';
  const opts = { day: '2-digit', month: '2-digit' };
  if (x.getFullYear() !== new Date().getFullYear()) opts.year = '2-digit';
  return x.toLocaleDateString('fr-FR', opts);
}

// Date sans heure (« 2026-09-29 ») : découpée telle quelle, sans passer par
// `new Date`, qui la lirait en UTC et pourrait reculer d'un jour.
function fmtJour(d) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d || '');
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '—';
}

function joursDepuis(d) {
  if (!d) return null;
  return Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 86400000));
}

function depuis(d) {
  const j = joursDepuis(d);
  if (j === null) return '';
  return j === 0 ? "aujourd'hui" : `depuis ${j} j`;
}

// Créer ou modifier un formulaire, la charte et l'observation sont des réglages
// d'administrateur (demande SELLENS du 26/10) : un utilisateur traite les
// réponses, il ne touche pas à ce qui part chez les clients. Le serveur
// refuse de toute façon (adminMiddleware) ; ici on n'affiche pas l'inutile.
const estAdmin = (user) => user?.role === 'admin' || user?.role === 'super-admin';

const MODE_BADGE = {
  off: { label: 'Off', classe: 'status-info' },
  simulation: { label: 'Observation', classe: 'status-warning' },
  actif: { label: 'Actif', classe: 'status-success' },
};

const CANAL_LIBELLE = { email: 'e-mail', sms: 'SMS', 'sms+email': 'SMS + e-mail' };
const TRAITEMENT_LIBELLE = { telephone: 'Tél.', sms: 'SMS', mail: 'E-mail', sur_place: 'Sur place', note: 'Note' };

function Suivi({ e }) {
  if (e.statut === 'echec') {
    return <><span className="status-error" title={e.erreur || ''}>Jamais parti</span>
      {e.erreur && <span className="sat-in-sous sat-in-tronque" title={e.erreur}>{e.erreur}</span>}</>;
  }
  if (e.statut !== 'repondu') {
    return <><span className="sat-in-attente">En attente</span><span className="sat-in-sous">{depuis(e.envoye_le)}</span></>;
  }
  if (e.traite_le) {
    const par = [TRAITEMENT_LIBELLE[e.canal_traitement],
      e.traite_par_prenom ? `${e.traite_par_prenom} ${(e.traite_par_nom || '').charAt(0)}.`.trim() : null,
      fmtCourt(e.traite_le)].filter(Boolean).join(' · ');
    return <><span className="status-success">✓ Traité</span><span className="sat-in-sous">{par}</span></>;
  }
  const detracteur = e.nps !== null && e.nps !== undefined && Number(e.nps) <= 8;
  return (
    <>
      <span className={detracteur ? 'status-error' : 'status-warning'}>{detracteur ? 'À rappeler' : 'À traiter'}</span>
      <span className="sat-in-sous">{depuis(e.repondu_le)}</span>
    </>
  );
}

// Canal d'envoi en clair, et la raison quand le planificateur l'a réduit
// (« SMS de 172 caractères : e-mail seul ») : un ⓘ au survol, au lieu d'une
// colonne presque toujours vide.
function CanalEnvoi({ e, prefixe }) {
  return (
    <span className="sat-in-sous">
      {prefixe}{CANAL_LIBELLE[e.canal_envoi] || e.canal_envoi || '—'}
      {e.note_canal && <span className="sat-in-info" title={e.note_canal} aria-label={e.note_canal}> ⓘ</span>}
    </span>
  );
}

// ─── Réponses et Envois : socle commun ──────────────────────────────────────
// Deux onglets (2026-10-19) : « Réponses » est la file de travail des
// opérationnels — uniquement des clients qui ont répondu — et « Envois » le
// journal de ce qui est parti (ou serait parti, en observation). Au quotidien
// on ne traite que des réponses : les envois sans retour noyaient la file.
function useListe({ mode, statut, file, tri, formulaireId, site, filtre }) {
  const [envois, setEnvois] = useState([]);
  const [stats, setStats] = useState(null);
  const [compteurs, setCompteurs] = useState(null);
  const [files, setFiles] = useState(null);
  const [loading, setLoading] = useState(true);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (mode) params.mode = mode;
      if (statut) params.statut = statut;
      if (file && file !== 'toutes') params.traitement = file;
      if (tri) params.tri = tri;
      if (formulaireId) params.formulaire_id = formulaireId;
      if (site) params.site = site;
      if (filtre) params.search = filtre;
      const [{ data: e }, { data: s }] = await Promise.all([api.getEnvois(params), api.getStats(params)]);
      setEnvois(e.envois || []);
      setCompteurs(e.compteurs || null);
      setFiles(e.files || null);
      setStats(s);
    } finally {
      setLoading(false);
    }
  }, [mode, statut, file, tri, formulaireId, site, filtre]);

  useEffect(() => { charger(); }, [charger]);
  return { envois, stats, compteurs, files, loading, charger };
}

function useSites() {
  const [sites, setSites] = useState([]);
  // Les points de vente ne dépendent pas des filtres : on les charge une fois.
  useEffect(() => { api.getSites().then(({ data }) => setSites(data.sites || [])).catch(() => {}); }, []);
  return sites;
}

const nf = (v, dec = 0) => (v === null || v === undefined ? '—'
  : Number(v).toLocaleString('fr-FR', { minimumFractionDigits: dec, maximumFractionDigits: dec }));

function ClientCellule({ e }) {
  return (
    <>
      <div className="sat-in-nom">{[e.client_prenom, e.client_nom].filter(Boolean).join(' ') || '—'}</div>
      {/* Véhicule sur sa propre ligne, sous le nom : les plaques s'alignent en
          colonne quelle que soit la longueur du nom (retour terrain du 03/11). */}
      {(e.immat || e.chassis) && (
        <div className="sat-in-vehicule"><VehiculeEnvoi immat={e.immat} chassis={e.chassis} /></div>
      )}
      <div className="sat-in-sous sat-in-ligne-sous">
        <span className="sat-in-tag" title={e.formulaire_nom}>{etiquetteFormulaire(e.formulaire_source, e.formulaire_nom)}</span>
        <span className="sat-in-site">{e.site_nom || e.site_code || ''}</span>
      </div>
    </>
  );
}

function FilesChips({ liste, files, file, onChoisir }) {
  if (!files) return null;
  return (
    <div className="filter-chips-bar sat-in-files">
      {liste.filter((f) => !f.siNonVide || files[f.cle] > 0).map((f) => (
        <button key={f.cle} type="button"
          className={`filter-chip ${file === f.cle ? 'chip-active' : 'chip-default'}`}
          onClick={() => onChoisir(f.cle)}>
          {f.label} <b className="sat-in-compte">{files[f.cle] ?? 0}</b>
        </button>
      ))}
    </div>
  );
}

function BarreFiltres({ filtre, setFiltre, formulaireId, setFormulaireId, site, setSite, sites, formulaires, selection, onSupprimer }) {
  return (
    <div className="page-toolbar">
      <input className="page-search" placeholder="🔍 Rechercher..." value={filtre}
        onChange={(e) => setFiltre(e.target.value)} />
      <select className={`page-select${formulaireId ? ' active-filter' : ''}`} value={formulaireId}
        onChange={(e) => setFormulaireId(e.target.value)}>
        <option value="">Tous les formulaires</option>
        {formulaires.map((f) => <option key={f.id} value={f.id}>{f.nom} — {f.concession_nom}</option>)}
      </select>
      {sites.length > 1 && (
        <select className={`page-select${site ? ' active-filter' : ''}`} value={site}
          onChange={(e) => setSite(e.target.value)}>
          <option value="">Tous les points de vente</option>
          {sites.map((x) => (
            <option key={x.site_code} value={x.site_code}>
              {x.site_nom || x.site_code} ({x.reponses}/{x.envois})
            </option>
          ))}
        </select>
      )}
      {onSupprimer && selection.length > 0 && (
        <button className="btn-secondary" onClick={onSupprimer}>
          🗑️ Supprimer la sélection ({selection.length})
        </button>
      )}
    </div>
  );
}

// Sélection + suppression (mode édition, super-admin), commune aux deux onglets.
function useSelection({ user, editMode, charger }) {
  const [selection, setSelection] = useState([]);
  const avecSelection = editMode && user?.role === 'super-admin';
  const supprimer = async () => {
    if (!window.confirm(`Supprimer ${selection.length} envoi(s) et leurs réponses ? Cette action est définitive.`)) return;
    await api.deleteEnvois(selection);
    setSelection([]);
    charger();
  };
  const caseSelection = (e) => avecSelection && (
    <td onClick={(ev) => ev.stopPropagation()}>
      <input type="checkbox" checked={selection.includes(e.id)}
        onChange={(ev) => setSelection(ev.target.checked
          ? [...selection, e.id] : selection.filter((x) => x !== e.id))} />
    </td>
  );
  return { selection, setSelection, avecSelection, supprimer, caseSelection };
}

// ─── Onglet Réponses ─────────────────────────────────────────────────────────
// Une boîte de réception d'avis (refonte du 2026-10-16) : la note, le client,
// ce qu'il a écrit, puis l'état du suivi. Seuls les clients qui ont RÉPONDU
// y figurent ; les files (« À traiter », « Détracteurs à traiter »…)
// affichent chacune leur volume.
const FILES_REPONSES = [
  { cle: 'a_traiter', label: 'À traiter' },
  { cle: 'detracteurs_a_traiter', label: 'Détracteurs à traiter' },
  { cle: 'traite', label: 'Traitées' },
  { cle: 'toutes', label: 'Toutes' },
];

function OngletReponses({ user, editMode, formulaires, onOuvrir }) {
  const [filtre, setFiltre] = useState('');
  const [file, setFile] = useState('a_traiter');
  const [formulaireId, setFormulaireId] = useState('');
  const [site, setSite] = useState('');
  const sites = useSites();
  const { envois, stats, files, loading, charger } = useListe({
    statut: 'repondu', file, formulaireId, site, filtre,
  });
  const { selection, setSelection, avecSelection, supprimer, caseSelection } = useSelection({ user, editMode, charger });
  const nbCol = (avecSelection ? 1 : 0) + 5;

  const messageVide = () => {
    if (files?.toutes === 0 && !filtre && !formulaireId && !site) {
      return 'Aucune réponse reçue pour le moment. Les messages partis et ceux en attente de retour sont dans l\'onglet « Envois ».';
    }
    if (file === 'a_traiter' || file === 'detracteurs_a_traiter') return 'Rien à traiter pour ces filtres.';
    return 'Aucune réponse pour ces filtres.';
  };

  return (
    <>
      <div className="sat-in-entete">
        <FilesChips liste={FILES_REPONSES} files={files} file={file}
          onChoisir={(cle) => { setSelection([]); setFile(cle); }} />
        {stats && (
          <div className="sat-in-chiffres">
            <span><b>{nf(stats.total_repondus)}</b> réponses</span>
            <span><b>{nf(stats.nps_moyen, 1)}</b> NPS moyen</span>
            {stats.note_moyenne !== null && <span><b>{nf(stats.note_moyenne, 1)}</b> note /5</span>}
          </div>
        )}
      </div>

      <BarreFiltres filtre={filtre} setFiltre={setFiltre} formulaireId={formulaireId} setFormulaireId={setFormulaireId}
        site={site} setSite={setSite} sites={sites} formulaires={formulaires}
        selection={selection} onSupprimer={avecSelection ? supprimer : null} />

      <div className="table-container sat-in-table">
        <table>
          <thead>
            <tr>
              {avecSelection && <th style={{ width: 36 }}></th>}
              <th className="sat-in-col-note">Note</th>
              <th className="sat-in-col-client">Client</th>
              <th>Ce qu'il a dit</th>
              <th className="sat-in-col-date">Répondu</th>
              <th className="sat-in-col-suivi">Suivi</th>
            </tr>
          </thead>
          <tbody>
            {envois.map((e) => (
              <tr key={e.id} onClick={() => onOuvrir(e.id)} className="sat-ligne-cliquable">
                {caseSelection(e)}
                <td><NotePastille nps={e.nps} statut={e.statut} /></td>
                <td><ClientCellule e={e} /></td>
                <td>
                  {e.verbatim
                    ? <div className="sat-in-verbatim">{e.verbatim}</div>
                    : <div className="sat-in-verbatim sat-in-verbatim-vide">Pas de commentaire</div>}
                </td>
                <td>
                  {/* Canal de la réponse (2026-10-22) : absent pour les anciens
                      envois SMS + e-mail, qui portaient le même lien. */}
                  <div className="sat-in-date">{fmtCourt(e.repondu_le)}{e.canal_reponse && ` · par ${CANAL_LIBELLE[e.canal_reponse] || e.canal_reponse}`}</div>
                  <CanalEnvoi e={e} prefixe={`envoyé ${fmtCourt(e.envoye_le)} · `} />
                </td>
                <td>
                  <div className="sat-in-suivi">
                    <Suivi e={e} />
                    {e.projet_declare && <EtatProjet statut={e.projet_statut} joignable={e.projet_joignable} />}
                  </div>
                </td>
              </tr>
            ))}
            {!loading && envois.length === 0 && (
              <tr><td colSpan={nbCol} className="sat-vide">{messageVide()}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ─── Onglet Projets d'achat (2026-11-02) ─────────────────────────────────────
// Les clients qui envisagent de changer de véhicule : une piste commerciale,
// suivie à part de l'avis (traiter l'avis ne dit pas qu'on a parlé du projet).
// Sans accord de recontact, la ligne reste visible mais ne se démarche pas.
const FILES_PROJETS = [
  { cle: 'a_rappeler', label: 'À rappeler' },
  { cle: 'rappeles', label: 'Rappelés' },
  { cle: 'sans_consentement', label: 'Sans accord de recontact', siNonVide: true },
  { cle: 'toutes', label: 'Tous' },
];
// Au-delà de 48 h sans rappel, le projet refroidit : l'ancienneté passe au rouge.
const DELAI_RAPPEL_JOURS = 2;

function SuiviProjet({ p }) {
  const issue = ISSUE_PROJET[p.projet_statut];
  if (!p.joignable) return <span className="sat-in-attente">Ne pas démarcher</span>;
  const par = [p.projet_par_prenom ? `${p.projet_par_prenom} ${(p.projet_par_nom || '').charAt(0)}.` : null,
    fmtCourt(p.projet_traite_le)].filter(Boolean).join(' · ');
  if (!issue) {
    const j = joursDepuis(p.repondu_le);
    return (
      <>
        <span className="status-warning">À rappeler</span>
        <span className={`sat-in-sous${j > DELAI_RAPPEL_JOURS ? ' sat-pa-retard' : ''}`}>{depuis(p.repondu_le)}</span>
      </>
    );
  }
  return (
    <>
      <span className={issue.classe}>{issue.label}</span>
      <span className="sat-in-sous">{par}</span>
      {p.projet_note && <span className="sat-in-sous sat-in-tronque" title={p.projet_note}>{p.projet_note}</span>}
    </>
  );
}

function OngletProjets({ user, editMode, formulaires, onOuvrir, version }) {
  const [filtre, setFiltre] = useState('');
  const [file, setFile] = useState('a_rappeler');
  const [formulaireId, setFormulaireId] = useState('');
  const [site, setSite] = useState('');
  const sites = useSites();
  const [projets, setProjets] = useState([]);
  const [files, setFiles] = useState(null);
  const [loading, setLoading] = useState(true);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (file !== 'toutes') params.file = file;
      if (formulaireId) params.formulaire_id = formulaireId;
      if (site) params.site = site;
      if (filtre) params.search = filtre;
      const { data } = await api.getProjets(params);
      setProjets(data.projets || []);
      setFiles(data.files || null);
    } finally {
      setLoading(false);
    }
  }, [file, formulaireId, site, filtre]);
  useEffect(() => { charger(); }, [charger, version]);

  const { selection, setSelection, avecSelection, supprimer, caseSelection } = useSelection({ user, editMode, charger });
  const nbCol = (avecSelection ? 1 : 0) + 5;

  const messageVide = () => {
    if (files?.toutes === 0 && !filtre && !formulaireId && !site) {
      return 'Aucun client n\'a encore déclaré de projet. Ils apparaîtront ici dès qu\'un client répondra « oui » à la question sur le changement de véhicule.';
    }
    if (file === 'a_rappeler') return 'Aucun projet à rappeler pour ces filtres.';
    return 'Aucun projet pour ces filtres.';
  };

  return (
    <>
      <div className="sat-in-entete">
        <FilesChips liste={FILES_PROJETS} files={files} file={file}
          onChoisir={(cle) => { setSelection([]); setFile(cle); }} />
        {files && (
          <div className="sat-in-chiffres">
            <span><b>{nf(files.toutes)}</b> projets déclarés</span>
            <span><b>{nf(files.toutes - files.sans_consentement)}</b> joignables</span>
          </div>
        )}
      </div>

      <BarreFiltres filtre={filtre} setFiltre={setFiltre} formulaireId={formulaireId} setFormulaireId={setFormulaireId}
        site={site} setSite={setSite} sites={sites} formulaires={formulaires}
        selection={selection} onSupprimer={avecSelection ? supprimer : null} />

      <div className="table-container sat-in-table">
        <table>
          <thead>
            <tr>
              {avecSelection && <th style={{ width: 36 }}></th>}
              <th className="sat-in-col-note">Note</th>
              <th className="sat-in-col-client">Client</th>
              <th>Accepte d'être recontacté par</th>
              <th className="sat-in-col-date">Répondu</th>
              <th className="sat-in-col-suivi">Suivi commercial</th>
            </tr>
          </thead>
          <tbody>
            {projets.map((p) => (
              <tr key={p.id} onClick={() => onOuvrir(p)}
                className={`sat-ligne-cliquable${p.joignable ? '' : ' sat-pa-grise'}`}>
                {caseSelection(p)}
                <td><NotePastille nps={p.nps} statut="repondu" /></td>
                <td>
                  <ClientCellule e={p} />
                  {p.marque && <span className="sat-in-sous">{p.marque}</span>}
                </td>
                <td>
                  {p.joignable
                    ? <div className="sat-pa-contacts">
                        {CONTACT_PROJET.filter((c) => p.choix?.[c.cle]).map((c) => (
                          <span key={c.cle} className="badge-pill">{c.label}</span>
                        ))}
                      </div>
                    : <span className="sat-in-attente">Pas d'accord donné</span>}
                </td>
                <td><div className="sat-in-date">{fmtCourt(p.repondu_le)}</div></td>
                <td><div className="sat-in-suivi"><SuiviProjet p={p} /></div></td>
              </tr>
            ))}
            {!loading && projets.length === 0 && (
              <tr><td colSpan={nbCol} className="sat-vide">{messageVide()}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ─── Onglet Envois ───────────────────────────────────────────────────────────
// Le journal des envois, en deux PÉRIMÈTRES : les envois réels, et
// l'observation (ce que le planificateur AURAIT envoyé). L'observation vivait
// dans un filtre d'état, où personne ne la trouvait : une vue vide alors que
// 155 lignes attendaient ailleurs se lisait comme une panne (constaté le
// 2026-10-05). D'où une bascule visible, avec les volumes.
const FILES_ENVOIS = [
  { cle: 'toutes', label: 'Tous' },
  { cle: 'en_attente', label: 'Sans réponse' },
  { cle: 'repondu', label: 'Répondus' },
  { cle: 'echec', label: "Échecs d'envoi", siNonVide: true },
];

function StatutEnvoi({ e }) {
  if (e.statut === 'echec') {
    return <><span className="status-error" title={e.erreur || ''}>Jamais parti</span>
      {e.erreur && <span className="sat-in-sous sat-in-tronque" title={e.erreur}>{e.erreur}</span>}</>;
  }
  if (e.statut === 'repondu') {
    return <><span className="status-success">Répondu</span><span className="sat-in-sous">le {fmtCourt(e.repondu_le)}</span></>;
  }
  return <><span className="sat-in-attente">Sans réponse</span><span className="sat-in-sous">{depuis(e.envoye_le)}</span></>;
}

function OngletEnvois({ user, editMode, formulaires, onOuvrir, onObserver }) {
  const peutObserver = estAdmin(user);
  const [perimetre, setPerimetre] = useState('actif');
  const choisi = useRef(false);
  const [filtre, setFiltre] = useState('');
  const [file, setFile] = useState('toutes');
  const [formulaireId, setFormulaireId] = useState('');
  const [site, setSite] = useState('');
  const sites = useSites();
  const enSimulation = perimetre === 'simulation';
  const { envois, stats, compteurs, files, loading, charger } = useListe({
    mode: enSimulation ? 'simulation' : null, file: enSimulation ? null : file, tri: 'envoi',
    formulaireId, site, filtre,
  });
  const { selection, setSelection, avecSelection, supprimer, caseSelection } = useSelection({ user, editMode, charger });

  // Aucun envoi réel mais de l'observation : on ouvre sur l'observation, une
  // seule fois — un choix explicite de l'utilisateur n'est jamais repris.
  useEffect(() => {
    if (choisi.current || !compteurs) return;
    choisi.current = true;
    if (peutObserver && !enSimulation && compteurs.actif === 0 && compteurs.simulation > 0) setPerimetre('simulation');
  }, [compteurs, enSimulation, peutObserver]);

  const basculer = (p) => { choisi.current = true; setSelection([]); setPerimetre(p); };
  const nbCol = (avecSelection ? 1 : 0) + (enSimulation ? 4 : 5);
  const taux = stats?.total_envoyes ? Math.round((100 * stats.total_repondus) / stats.total_envoyes) : null;

  const messageVide = () => {
    if (enSimulation) return 'Aucune ligne d\'observation. Le planificateur en consigne une par destinataire, chaque jour, pour un formulaire en mode « observation ».';
    if (peutObserver && compteurs?.actif === 0 && compteurs?.simulation > 0) {
      return `Aucun envoi réel — ${compteurs.simulation} ligne(s) en observation : basculez sur « 👁️ Observation ».`;
    }
    return 'Aucun envoi pour ces filtres.';
  };

  return (
    <>
      <div className="sat-in-entete">
        {peutObserver && (
        <div className="header-segmented sat-perimetre">
          <button type="button" className={`header-segmented-btn${!enSimulation ? ' active' : ''}`}
            onClick={() => basculer('actif')}>
            📨 Envois réels{compteurs ? ` (${compteurs.actif})` : ''}
          </button>
          <button type="button" className={`header-segmented-btn${enSimulation ? ' active' : ''}`}
            onClick={() => basculer('simulation')}>
            👁️ Observation{compteurs ? ` (${compteurs.simulation})` : ''}
          </button>
        </div>
        )}
        {stats && !enSimulation && (
          <div className="sat-in-chiffres">
            <span><b>{nf(stats.total_envoyes)}</b> envoyés</span>
            <span><b>{nf(stats.total_repondus)}</b> réponses{taux !== null ? ` (${taux} %)` : ''}</span>
          </div>
        )}
      </div>

      {enSimulation && (
        <div className="status-warning sat-bandeau">
          👁️ <b>Phase d'observation.</b> Ces lignes sont ce que le planificateur <b>aurait envoyé</b> :
          aucun message n'est parti, aucun de ces clients n'a été contacté, et aucune réponse ne viendra.
          Cliquez sur une ligne pour lire le message exact qu'il aurait reçu.
        </div>
      )}

      {!enSimulation && (
        <FilesChips liste={FILES_ENVOIS} files={files} file={file}
          onChoisir={(cle) => { setSelection([]); setFile(cle); }} />
      )}

      <BarreFiltres filtre={filtre} setFiltre={setFiltre} formulaireId={formulaireId} setFormulaireId={setFormulaireId}
        site={site} setSite={setSite} sites={sites} formulaires={formulaires}
        selection={selection} onSupprimer={avecSelection ? supprimer : null} />

      <div className="table-container sat-in-table">
        <table>
          <thead>
            <tr>
              {avecSelection && <th style={{ width: 36 }}></th>}
              <th className="sat-in-col-client">Client</th>
              <th title="Date de vente du véhicule (VN) ou de facture du passage atelier (APV) : celle dont part le délai du formulaire">Vente / passage</th>
              <th>{enSimulation ? 'Calculé le' : 'Envoyé le'}</th>
              <th>Canal</th>
              {!enSimulation && <th className="sat-in-col-suivi">Statut</th>}
            </tr>
          </thead>
          <tbody>
            {envois.map((e) => (
              <tr key={e.id} onClick={() => (enSimulation ? onObserver(e.id) : onOuvrir(e.id))} className="sat-ligne-cliquable">
                {caseSelection(e)}
                <td><ClientCellule e={e} /></td>
                <td className={e.date_vente ? '' : 'sat-vide-inline'}>{fmtJour(e.date_vente)}</td>
                <td>{fmtCourt(e.envoye_le)}</td>
                <td><CanalEnvoi e={e} /></td>
                {!enSimulation && <td><div className="sat-in-suivi"><StatutEnvoi e={e} /></div></td>}
              </tr>
            ))}
            {!loading && envois.length === 0 && (
              <tr><td colSpan={nbCol} className="sat-vide">{messageVide()}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ─── Onglet Formulaires ──────────────────────────────────────────────────────
function OngletFormulaires({ user, formulaires, catalogue, onRecharger, onEditer }) {
  const libelleSource = (cle) => (catalogue?.sources || []).find((s) => s.cle === cle)?.label || cle;
  const isSuperAdmin = user?.role === 'super-admin';
  const isAdmin = estAdmin(user);

  const dupliquer = async (id) => { await api.dupliquer(id); onRecharger(); };
  const supprimer = async (f) => {
    if (!window.confirm(`Supprimer le formulaire « ${f.nom} » ?`)) return;
    try {
      await api.deleteFormulaire(f.id);
      onRecharger();
    } catch (e) {
      window.alert(e.response?.data?.error || 'Suppression impossible');
    }
  };

  return (
    <div className="table-container">
      <table>
        <thead>
          <tr>
            <th>Formulaire</th>
            <th>Concession</th>
            <th>Questions</th>
            <th>Source</th>
            <th>Fréquence</th>
            <th>Mode</th>
            <th>Envois</th>
            <th>Réponses</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {formulaires.map((f) => {
            const badge = MODE_BADGE[f.mode] || MODE_BADGE.off;
            return (
              <tr key={f.id}>
                <td>
                  {f.nom}
                  {!f.actif && <span className="status-info sat-pastille">inactif</span>}
                </td>
                <td>{f.concession_nom}</td>
                <td>{f.nb_questions}</td>
                <td>{libelleSource(f.source)}</td>
                <td>{String(f.heure_envoi || '').slice(0, 5)} · {f.timezone?.split('/')[1]}</td>
                <td><span className={badge.classe}>{badge.label}</span></td>
                <td>{f.nb_envois}</td>
                <td>{f.nb_reponses}</td>
                <td>
                  <div className="action-buttons">
                    {isAdmin && (
                      <>
                        <button className="btn-icon" title="Modifier" onClick={() => onEditer(f.id)}>✏️</button>
                        <button className="btn-icon" title="Dupliquer" onClick={() => dupliquer(f.id)}>📄</button>
                      </>
                    )}
                    {isSuperAdmin && (
                      <button className="btn-icon delete" title="Supprimer" onClick={() => supprimer(f)}>🗑️</button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
          {formulaires.length === 0 && (
            <tr><td colSpan={9} className="sat-vide">Aucun formulaire. Créez-en un pour commencer.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────
export default function Satisfaction({ user, concessions, setHeaderAction, editMode }) {
  // On arrive sur le tableau de bord : la première question est « où en
  // sommes-nous ? », pas « quelle est la 1 400e réponse ? ».
  const [onglet, setOnglet] = useState('reponses');
  const [formulaires, setFormulaires] = useState([]);
  const [catalogue, setCatalogue] = useState(null);
  const [editionId, setEditionId] = useState(null);
  const [nouveau, setNouveau] = useState(false);
  const [reponseId, setReponseId] = useState(null);
  const [observationId, setObservationId] = useState(null);
  const [masques, setMasques] = useState(0);
  const [rafraichir, setRafraichir] = useState(0);
  // Pastilles des onglets : réponses à traiter, projets à rappeler. Rechargées
  // à chaque action (rafraichir), comme les listes.
  const [compteurs, setCompteurs] = useState(null);
  useEffect(() => {
    api.getCompteurs().then(({ data }) => setCompteurs(data)).catch(() => {});
  }, [rafraichir]);

  const chargerFormulaires = useCallback(async () => {
    const { data } = await api.getFormulaires();
    setFormulaires(data.formulaires || []);
    setMasques(data.masques || 0);
  }, []);

  // Concession du sélecteur global (super-admin / téléopérateur). Elle filtre
  // TOUTE la page sans que rien ne le dise ici : on l'annonce dès qu'elle
  // masque quelque chose.
  const idFiltre = (() => { try { return localStorage.getItem('crm-global-concession'); } catch { return null; } })();
  const nomFiltre = idFiltre ? (concessions || []).find((c) => String(c.id) === String(idFiltre))?.nom : null;

  useEffect(() => { chargerFormulaires(); }, [chargerFormulaires, rafraichir]);
  useEffect(() => { api.getCatalogue().then(({ data }) => setCatalogue(data)).catch(() => {}); }, []);

  // La création passe par une modale et non par des `window.prompt` : elle doit
  // faire choisir la SOURCE des destinataires, qui commande tout le reste du
  // formulaire (délai, quota, variables du message).
  const creer = useCallback(async ({ nom, concession_id, source }) => {
    const conc = user?.concession_id || concession_id
      || (concessions?.length === 1 ? concessions[0].id : null);
    const { data } = await api.createFormulaire({ nom, concession_id: conc, source });
    setNouveau(false);
    setEditionId(data.formulaire.id);
    setRafraichir((n) => n + 1);
  }, [user, concessions]);

  const isAdmin = estAdmin(user);
  useEffect(() => {
    if (setHeaderAction && isAdmin) {
      setHeaderAction({ primary: { label: '+ Nouveau formulaire', onClick: () => setNouveau(true) } });
    }
  }, [setHeaderAction, isAdmin]);

  return (
    <div className="page-container">
      <div className="page-tabs">
        <button className={onglet === 'reponses' ? 'active' : ''} onClick={() => setOnglet('reponses')}>
          📥 Réponses
          {compteurs?.a_traiter > 0 && <span className="tab-count" title="Réponses à traiter">{compteurs.a_traiter}</span>}
        </button>
        <button className={onglet === 'projets' ? 'active' : ''} onClick={() => setOnglet('projets')}>
          🚗 Projets d'achat
          {compteurs?.projets_a_rappeler > 0 && <span className="tab-count" title="Projets à rappeler">{compteurs.projets_a_rappeler}</span>}
        </button>
        <button className={onglet === 'envois' ? 'active' : ''} onClick={() => setOnglet('envois')}>
          📨 Envois
        </button>
        <button className={onglet === 'bord' ? 'active' : ''} onClick={() => setOnglet('bord')}>
          📊 Tableau de bord
        </button>
        <button className={onglet === 'formulaires' ? 'active' : ''} onClick={() => setOnglet('formulaires')}>
          ⚙️ Formulaires
        </button>
        {isAdmin && (
          <button className={onglet === 'charte' ? 'active' : ''} onClick={() => setOnglet('charte')}>
            🎨 Charte
          </button>
        )}
      </div>

      {masques > 0 && (
        <div className="status-info sat-bandeau">
          🔎 Page filtrée sur <b>{nomFiltre || 'une concession'}</b> : {masques} formulaire{masques > 1 ? 's' : ''} d'autres
          concessions {masques > 1 ? 'sont masqués' : 'est masqué'}, ainsi que leurs envois.
          Pour tout voir, choisissez « Toutes les concessions » dans le bloc Concession en haut du menu.
        </div>
      )}

      {onglet === 'bord' && <SatisfactionTableauBord formulaires={formulaires} />}
      {onglet === 'reponses' && (
        <OngletReponses key={rafraichir} user={user} editMode={editMode}
          formulaires={formulaires} onOuvrir={setReponseId} />
      )}
      {onglet === 'projets' && (
        <OngletProjets user={user} editMode={editMode} formulaires={formulaires}
          onOuvrir={(p) => setReponseId(p.id)} version={rafraichir} />
      )}
      {onglet === 'envois' && (
        <OngletEnvois key={rafraichir} user={user} editMode={editMode}
          formulaires={formulaires} onOuvrir={setReponseId} onObserver={setObservationId} />
      )}
      {onglet === 'formulaires' && (
        <OngletFormulaires user={user} formulaires={formulaires} catalogue={catalogue}
          onRecharger={() => setRafraichir((n) => n + 1)} onEditer={setEditionId} />
      )}

      {onglet === 'charte' && isAdmin && <SatisfactionCharte user={user} formulaires={formulaires} editMode={editMode} />}

      {nouveau && (
        <SatisfactionNouveauModal
          user={user} concessions={concessions} catalogue={catalogue}
          onClose={() => setNouveau(false)} onCreer={creer}
        />
      )}
      {editionId && (
        <SatisfactionFormulaireEditor
          formulaireId={editionId} catalogue={catalogue} user={user}
          onClose={() => setEditionId(null)}
          onSaved={() => setRafraichir((n) => n + 1)}
        />
      )}
      {observationId && (
        <SatisfactionObservationModal envoiId={observationId} onClose={() => setObservationId(null)} />
      )}
      {reponseId && (
        <SatisfactionReponseModal
          envoiId={reponseId} user={user}
          onClose={() => setReponseId(null)}
          onChanged={() => setRafraichir((n) => n + 1)}
        />
      )}
    </div>
  );
}
